import angular from 'angular'
import browser from '../utils/browser'

import { round } from 'lodash-es'
import { toRgb, toPosPixel } from '../utils/bits'

angular
  .module('ggs.gallery-preview-flow')
  .factory('GalleryPreviewFlowStyleHandlers', GalleryPreviewFlowStyleHandlersProvider)

function GalleryPreviewFlowStyleHandlersProvider(
  rootQs,
  GSNApplyFuncTmpl,
  GSNApplyStyleTmpl) {

  const OWNER_TAG = __filename

  return function GalleryPreviewFlowStyleHandlers(extFlowResize) {

    // applyLayoutChangingParams

    const applyLayoutChangingParams = GSNApplyFuncTmpl({
      propKeys: [
        'adaptive_static_rows',
        'adaptive_static_rows_use',
        'carousel_classic_rows',
        'carousel_fill_viewport_fraction',
        'carousel_layout_type',
        'carousel_mode',
        'carousel_scrollbar_show',
        'carousel_scrollbar_size',
        'carousel_showcase_min_height',
        'carousel_static_row_height',
        'carousel_static_rows',
        'custom_preview_grid',
        'custom_preview_grid_use',
        'custom_preview_height',
        'custom_preview_height_use',
        'custom_preview_width',
        'custom_preview_width_use',
        'highlight_period',
        'normalize_preview',
        'post_width_option',
        'show_source_in_preview',
        'show_user_info_in_preview',
        'square_preview'
      ],
      ownerTag: OWNER_TAG,
      userFunc: extFlowResize
    })

    // applyPreviewStrokeColor

    const STROKE_COLOR_PLH = ':stroke-color:'
    const STROKE_COLOR_TMPL = `
      ${rootQs} .ggs-gallery-preview {
        background: ${STROKE_COLOR_PLH};
        border-color: ${STROKE_COLOR_PLH};
      }`

    const applyPreviewStrokeColor = GSNApplyStyleTmpl({
      allowKey: 'preview_stroke_show',
      propKeys: 'preview_stroke_color',
      ownerTag: OWNER_TAG,
      styleTmpl: STROKE_COLOR_TMPL,
      stylePlh: STROKE_COLOR_PLH
    })

    // applyCarouselButtons

    const BUTTONS_DISPLAY_PLH = ':buttons-display:'
    const BUTTONS_DISPLAY_TMPL = `
      ${rootQs} .ggs-gallery-preview-flow__carousel-button {
        display: ${BUTTONS_DISPLAY_PLH};
      }`

    const applyCarouselButtons = GSNApplyStyleTmpl({
      propKeys: 'carousel_mode',
      ownerTag: OWNER_TAG,
      styleFunc: (carousel_mode) => {
        const display = carousel_mode && browser.likeDesktop
        const cssValue = display ? 'block' : 'none'
        return BUTTONS_DISPLAY_TMPL.replace(BUTTONS_DISPLAY_PLH, cssValue)
      }
    })

    // applyCarouselNativeScrollbarStyle

    const SCROLLBAR_SIZE_PLH = ':scrollbar-size:'
    const SCROLLBAR_OVERFLOW_X_PLH = ':scrollbar-overflow-x:'
    const SCROLLBAR_BORDER_RADIUS_PLH = ':scrollbar-border-radius:'

    const SCROLLBAR_TRACK_COLOR_PLH = ':scrollbar-track-color:'
    const SCROLLBAR_THUMB_COLOR_PLH = ':scrollbar-thumb-color:'

    const SCROLLBAR_TRACK_OPACITY_PLH = ':scrollbar-track-opacity:'
    const SCROLLBAR_THUMB_OPACITY_PLH = ':scrollbar-thumb-opacity:'

    const SCROLLBAR_TRACK_OPACITY_HOVER_PLH = ':scrollbar-track-opacity-hover:'
    const SCROLLBAR_THUMB_OPACITY_HOVER_PLH = ':scrollbar-thumb-opacity-hover:'

    const SHOW_STYLED_NATIVE_SCROLLBAR = `
      ${rootQs} .ggs-gallery-preview-flow__isotope-grid-showcase {
        overflow-x: ${SCROLLBAR_OVERFLOW_X_PLH};
        overflow-y: hidden;
        -webkit-overflow-scrolling: touch;
      }

      @supports not selector(::-webkit-scrollbar) {
        ${rootQs} .ggs-gallery-preview-flow__isotope-grid-showcase {
          scrollbar-width: thin;
          scrollbar-color: rgba(${SCROLLBAR_THUMB_COLOR_PLH}, ${SCROLLBAR_THUMB_OPACITY_PLH}) transparent;
        }
      }

      ${rootQs} .ggs-gallery-preview-flow__isotope-grid-showcase::-webkit-scrollbar:vertical {
        width: ${SCROLLBAR_SIZE_PLH};
      }

      ${rootQs} .ggs-gallery-preview-flow__isotope-grid-showcase::-webkit-scrollbar:horizontal {
        height: ${SCROLLBAR_SIZE_PLH};
      }

      ${rootQs} .ggs-gallery-preview-flow__isotope-grid-showcase::-webkit-scrollbar {
        border-radius: ${SCROLLBAR_BORDER_RADIUS_PLH};
        background-color: rgba(${SCROLLBAR_TRACK_COLOR_PLH}, ${SCROLLBAR_TRACK_OPACITY_PLH});
      }

      ${rootQs} .ggs-gallery-preview-flow__isotope-grid-showcase::-webkit-scrollbar:hover {
        background-color: rgba(${SCROLLBAR_TRACK_COLOR_PLH}, ${SCROLLBAR_TRACK_OPACITY_HOVER_PLH});
      }

      ${rootQs} .ggs-gallery-preview-flow__isotope-grid-showcase::-webkit-scrollbar-thumb {
        border-radius: ${SCROLLBAR_BORDER_RADIUS_PLH};
        background-color: rgba(${SCROLLBAR_THUMB_COLOR_PLH}, ${SCROLLBAR_THUMB_OPACITY_PLH});
      }

      ${rootQs} .ggs-gallery-preview-flow__isotope-grid-showcase::-webkit-scrollbar-thumb:hover {
        background-color: rgba(${SCROLLBAR_THUMB_COLOR_PLH}, ${SCROLLBAR_THUMB_OPACITY_HOVER_PLH});
      }`

    const COLLAPSE_NATIVE_SCROLLBAR = `
      ${rootQs} .ggs-gallery-preview-flow__isotope-grid-showcase {
        overflow-x: scroll;
        overflow-y: hidden;
        scrollbar-width: none;
        -webkit-overflow-scrolling: touch;
      }

      ${rootQs} .ggs-gallery-preview-flow__isotope-grid-showcase::-webkit-scrollbar {
        width: 0px;
        height: 0px;
      }`

    const applyCarouselNativeScrollbarStyle = GSNApplyStyleTmpl({
      propKeys: [
        'carousel_mode',
        'carousel_scrollbar_show',
        'carousel_scrollbar_size',
        'carousel_scrollbar_overlay',
        'carousel_scrollbar_track_color',
        'carousel_scrollbar_thumb_color',
        'carousel_scrollbar_track_opacity',
        'carousel_scrollbar_thumb_opacity'
      ],
      ownerTag: OWNER_TAG,
      allowEmpty: false,
      styleFunc(
        carousel_mode,
        carousel_scrollbar_show,
        carousel_scrollbar_size,
        carousel_scrollbar_overlay,
        carousel_scrollbar_track_color,
        carousel_scrollbar_thumb_color,
        carousel_scrollbar_track_opacity,
        carousel_scrollbar_thumb_opacity) {

        if (carousel_mode && carousel_scrollbar_show) {
          const borderRadius = Math.round(carousel_scrollbar_size / 2)

          const scrollbarSizeCss = toPosPixel(carousel_scrollbar_size)
          const borderRadiusCss = toPosPixel(borderRadius)
          const overflowXCss = carousel_scrollbar_overlay ? 'overlay' : 'auto'

          return SHOW_STYLED_NATIVE_SCROLLBAR
            .replace(RegExp(SCROLLBAR_SIZE_PLH, 'g'), scrollbarSizeCss)
            .replace(RegExp(SCROLLBAR_BORDER_RADIUS_PLH, 'g'), borderRadiusCss)
            .replace(RegExp(SCROLLBAR_OVERFLOW_X_PLH, 'g'), overflowXCss)
            .replace(RegExp(SCROLLBAR_TRACK_COLOR_PLH, 'g'), toRgb(carousel_scrollbar_track_color))
            .replace(RegExp(SCROLLBAR_THUMB_COLOR_PLH, 'g'), toRgb(carousel_scrollbar_thumb_color))
            .replace(RegExp(SCROLLBAR_TRACK_OPACITY_PLH, 'g'), carousel_scrollbar_track_opacity)
            .replace(RegExp(SCROLLBAR_THUMB_OPACITY_PLH, 'g'), carousel_scrollbar_thumb_opacity)
            .replace(RegExp(SCROLLBAR_TRACK_OPACITY_HOVER_PLH, 'g'), round(1.5 * carousel_scrollbar_track_opacity, 3))
            .replace(RegExp(SCROLLBAR_THUMB_OPACITY_HOVER_PLH, 'g'), round(1.5 * carousel_scrollbar_thumb_opacity, 3))
        } else if (carousel_mode) {
          return COLLAPSE_NATIVE_SCROLLBAR
        }
      }
    })

    // return

    return [
      applyLayoutChangingParams,
      applyPreviewStrokeColor,
      applyCarouselButtons,
      applyCarouselNativeScrollbarStyle
    ]
  }
}
